import { RATING_LIMITS, RATING_REASONS } from "@hermes/shared";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useId, useState } from "react";
import { chatsApi } from "../../api/chats";
import type { ChatHistory, RatingReason, RatingValue } from "../../api/types";
import { ThumbDownIcon, ThumbUpIcon } from "../../ui/icons";
import { toast } from "../../ui/toast";

const ease = [0.22, 1, 0.36, 1] as const;
const spring = { type: "spring", stiffness: 320, damping: 28, mass: 0.8 } as const;

export const REASON_LABEL: Record<RatingReason, string> = {
  inaccurate: "Неточно или с ошибкой",
  incomplete: "Неполно",
  off_topic: "Не по вопросу",
  file: "Проблема с файлом",
  style: "Не тот тон или формат",
  slow: "Слишком долго",
  other: "Другое",
};

const field =
  "w-full rounded-control border bg-surface px-3.5 text-base focus:outline-none focus-visible:outline-none border-line focus:border-accent focus:shadow-[0_0_0_4px_rgb(23_128_60/16%)]";

/**
 * 👍 / 👎 под ответом. Оценка сохраняется сразу; после 👎 открывается карточка «что было не так»
 * с причинами и комментарием — необязательная. Повторное нажатие на выбранную оценку снимает её.
 */
export function RateButtons({ chatId, runId, rating, temporary }: { chatId: string; runId: string; rating: RatingValue | undefined; temporary: boolean }) {
  const qc = useQueryClient();
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  const panelId = useId();

  /** Оценка сразу видна в истории чата; при ошибке — откат. */
  const setLocal = (value: RatingValue | undefined) =>
    qc.setQueryData<ChatHistory>(["messages", chatId], (old) =>
      old && {
        ...old,
        messages: old.messages.map((m) => {
          if (m.runId !== runId) return m;
          const { rating: _r, ...rest } = m;
          return value ? { ...rest, rating: value } : rest;
        }),
      },
    );

  const choose = async (value: RatingValue) => {
    const prev = rating;
    if (prev === value) {
      setOpen(false);
      setLocal(undefined);
      try {
        await chatsApi.unrate(runId);
      } catch {
        setLocal(prev);
        toast("Не удалось снять оценку. Попробуйте ещё раз.");
      }
      return;
    }
    setLocal(value);
    setOpen(value === "down");
    try {
      await chatsApi.rate(runId, { value });
      if (value === "up") toast("Спасибо за оценку");
    } catch {
      setLocal(prev);
      setOpen(false);
      toast("Не удалось сохранить оценку. Попробуйте ещё раз.");
    }
  };

  const thumb = (value: RatingValue, label: string, Icon: typeof ThumbUpIcon) => {
    const on = rating === value;
    return (
      <button
        type="button"
        aria-label={label}
        aria-pressed={on}
        {...(value === "down" ? { "aria-expanded": open, "aria-controls": panelId } : {})}
        onClick={() => void choose(value)}
        className={`pressable relative grid size-11 place-items-center rounded-[10px] transition-colors hover:bg-bg [&_svg]:size-4 ${on ? "text-accent-ink" : "text-sub hover:text-ink"}`}
      >
        <AnimatePresence initial={false}>
          {on && (
            <motion.span
              aria-hidden
              className="absolute inset-1.5 rounded-[9px] bg-accent-soft"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.7 }}
              transition={reduce ? { duration: 0 } : spring}
            />
          )}
        </AnimatePresence>
        <motion.span
          className="relative"
          key={on ? "on" : "off"}
          initial={reduce || !on ? false : { scale: 0.6, rotate: value === "up" ? -14 : 14 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={spring}
        >
          <Icon />
        </motion.span>
      </button>
    );
  };

  return (
    <>
      <span className="inline-flex items-center" role="group" aria-label="Оценка ответа">
        {thumb("up", "Хороший ответ", ThumbUpIcon)}
        {thumb("down", "Плохой ответ", ThumbDownIcon)}
      </span>
      <AnimatePresence initial={false}>
        {open && rating === "down" && (
          <motion.div
            key="why"
            id={panelId}
            initial={reduce ? { opacity: 0 } : { opacity: 0, height: 0 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, height: "auto" }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, height: 0 }}
            transition={{ duration: 0.26, ease }}
            className="basis-full overflow-hidden"
          >
            <WhyPanel runId={runId} temporary={temporary} onDone={() => setOpen(false)} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/** Необязательные подробности к 👎: причины-чипы и комментарий. */
function WhyPanel({ runId, temporary, onDone }: { runId: string; temporary: boolean; onDone: () => void }) {
  const reduce = useReducedMotion();
  const [reasons, setReasons] = useState<RatingReason[]>([]);
  const [comment, setComment] = useState("");
  const [share, setShare] = useState(false);
  const [sending, setSending] = useState(false);
  const ids = { title: useId(), text: useId(), note: useId(), share: useId() };
  const toggle = (r: RatingReason) => setReasons((list) => (list.includes(r) ? list.filter((x) => x !== r) : [...list, r]));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reasons.length && !comment.trim() && !share) return onDone();
    setSending(true);
    try {
      await chatsApi.rate(runId, { value: "down", reasons, ...(comment.trim() ? { comment: comment.trim() } : {}), ...(share ? { share } : {}) });
      toast("Спасибо, учтём");
      onDone();
    } catch {
      toast("Не удалось отправить. Попробуйте ещё раз.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={(e) => void submit(e)}
      aria-labelledby={ids.title}
      className="mt-1 mb-2 ml-2.5 rounded-card border border-line bg-surface p-4 shadow-card md:p-5"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onDone();
        }
      }}
    >
      <p id={ids.title} className="text-[15px] font-semibold">
        Что было не так?
      </p>
      <p id={ids.note} className="mt-0.5 text-[13px] text-sub">
        {temporary
          ? "Необязательно. Чат временный: администратор увидит только оценку и ваш комментарий."
          : "Необязательно. Администратор сервиса увидит оценку и комментарий, но не переписку."}
      </p>
      <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Причины">
        {RATING_REASONS.map((r, i) => {
          const on = reasons.includes(r);
          return (
            <motion.button
              key={r}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(r)}
              initial={reduce ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.24, ease, delay: reduce ? 0 : Math.min(i * 0.04, 0.24) }}
              className={`pressable h-9 rounded-full border px-3.5 text-sm font-medium transition-colors duration-200 pointer-coarse:h-11 ${
                on ? "border-accent-line bg-accent-soft text-accent-ink" : "border-line bg-surface text-ink-2 hover:bg-hover"
              }`}
            >
              {REASON_LABEL[r]}
            </motion.button>
          );
        })}
      </div>
      <label htmlFor={ids.text} className="sr-only">
        Комментарий
      </label>
      <textarea
        id={ids.text}
        rows={2}
        maxLength={RATING_LIMITS.comment}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Чего не хватило или что исправить"
        aria-describedby={ids.note}
        className={`${field} mt-3 block min-h-[72px] resize-y py-2.5 text-[15px] leading-[22px] placeholder:text-placeholder`}
      />
      {!temporary && (
        <label htmlFor={ids.share} className="mt-2 flex min-h-11 cursor-pointer items-center gap-2.5 text-sm text-ink-2">
          <input
            id={ids.share}
            type="checkbox"
            checked={share}
            onChange={(e) => setShare(e.target.checked)}
            className="size-5 flex-none accent-[var(--color-accent)]"
          />
          Приложить вопрос и ответ, чтобы было понятнее
        </label>
      )}
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onDone} className="pressable inline-flex h-11 items-center rounded-control px-4 text-sm font-semibold text-ink-2 hover:bg-hover">
          Не сейчас
        </button>
        <button
          type="submit"
          disabled={sending}
          className="pressable inline-flex h-11 items-center rounded-control bg-accent-grad px-5 text-sm font-semibold text-white disabled:opacity-60"
        >
          {sending ? "Отправляю…" : "Отправить"}
        </button>
      </div>
    </form>
  );
}
